/**
 * TargetingBorder - Unified targeting border component
 * Provides consistent yellow border with subtle pulsating effect for all targeting modes
 */

import React from 'react';
import './TargetingBorder.css';

/** Inline border for unit/card media (overflow:hidden clips outline). */
export const TARGETING_YELLOW_BORDER = '3px solid #ffff00';

/**
 * CSS border string for card images and hand units during targeting.
 * Zones use getTargetingBorderStyles (outline) so separator borders stay intact.
 * @param {boolean} isActive
 * @returns {string|null}
 */
export function getTargetingMediaBorderCss(isActive) {
  return isActive ? TARGETING_YELLOW_BORDER : null;
}

/**
 * Get targeting border styles for inline use
 * @param {boolean} isActive - Whether targeting is active
 * @returns {Object|null} - Style object or null
 */
export function getTargetingBorderStyles(isActive) {
  if (!isActive) return null;
  // Use outline instead of border so zone separator lines (borderRight/borderLeft) are never overwritten
  return {
    outline: '3px solid #ffff00',
    outlineOffset: '0',
    cursor: 'pointer',
    position: 'relative'
  };
}

/**
 * Get targeting border className for class-based styling
 * @param {boolean} isActive - Whether targeting is active
 * @returns {string} - ClassName string
 */
export function getTargetingBorderClassName(isActive) {
  return isActive ? 'targeting-border' : '';
}

/**
 * TargetingBorder wrapper component
 * Wraps children with targeting border styling when active
 * @param {Object} props
 * @param {boolean} props.isActive - Whether targeting is active
 * @param {React.ReactNode} props.children - Children to wrap
 * @param {Object} props.style - Additional inline styles
 * @param {string} props.className - Additional className
 */
export function TargetingBorder({ isActive, children, style = {}, className = '' }) {
  const combinedClassName = `${className} ${getTargetingBorderClassName(isActive)}`.trim();
  const combinedStyle = {
    ...style,
    ...getTargetingBorderStyles(isActive)
  };

  return (
    <div className={combinedClassName} style={combinedStyle}>
      {children}
    </div>
  );
}

export default TargetingBorder;

